import Head from 'next/head';
import { SignupPage, SignupPageProps } from '@zapier/auth-pages';
import type { InvitationResponse, Invitation } from '@zapier/authentication';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import {
  getFacebookLeadAdsRedirectUrl,
  isFacebookLeadAdsUrl,
} from '../../security/securityHeaders';
import {
  getServerSideSplitId,
  getServerSideSplitSingleton,
  FEATURE_FLAGS,
} from '@zapier/identity-utils';

import { PageLayout, Title } from '../../components';

import { init as initBuilderKit, safelyFetchSection } from '../../builder-kit';

initBuilderKit();

export default function SignUp({
  builderSections,
  invitationData,
}: SignupPageProps) {
  return (
    <>
      <Head>
        <script src="https://cdn.zapier.com/packages/intercom/v1/index.js" />
      </Head>
      <Title title="Sign up">
        <PageLayout>
          <SignupPage
            builderSections={builderSections}
            invitationData={invitationData}
          />
        </PageLayout>
      </Title>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<SignupPageProps> = async (
  context: GetServerSidePropsContext
) => {
  const reqUrl = context.req.url || '';
  const parsedUrl = new URL(`https://zapier.com${reqUrl}`);

  const { client } = getServerSideSplitSingleton();
  await client().ready();
  const treatments = client().getTreatmentsWithConfig(
    getServerSideSplitId(context),
    Object.values(FEATURE_FLAGS)
  );

  // Get content from Builder.io
  const models = ['sign-up-left-side', 'sign-up-footer'];
  const builderSections = await Promise.all(
    models.map(model =>
      safelyFetchSection({
        model,
        userAttributes: {
          queryParams: encodeURIComponent(parsedUrl.search),
          urlPath: parsedUrl.pathname,
        },
      })
    )
  ).then(([leftSide, footer]) => {
    return {
      leftSide,
      footer,
    };
  });

  try {
    if (isFacebookLeadAdsUrl(reqUrl)) {
      const fbUrl = await getFacebookLeadAdsRedirectUrl(reqUrl);
      return {
        redirect: {
          permanent: false,
          destination: fbUrl,
        },
        props: {},
      };
    }
  } catch (err) {
    console.error(
      'Error trying to redirect to Facebook Lead Ads landing page',
      err
    );
  }

  try {
    const isCompanyAccountSetup = (context.query.next || '').includes(
      'app/company-account-setup'
    );
    const isCompanySalesRedirectOn =
      treatments[FEATURE_FLAGS.COMPANY_SALES_REDIRECT]?.treatment === 'on';

    if (isCompanyAccountSetup && isCompanySalesRedirectOn) {
      return {
        redirect: {
          destination:
            '/l/contact-sales?demo_source=original-company-account-setup',
        },
        props: {},
      };
    }
  } catch (err) {
    console.error(
      'Error trying to redirect to contact sales landing page',
      err
    );
  }

  const code = context.query.invitation as string;
  if (code) {
    try {
      const invitation = await getInvitationByCode(context, code);

      return {
        props: {
          builderSections,
          invitationData: {
            invitation,
            isValid: Boolean(invitation?.status === 'pending'),
            code,
          },
          treatments,
        },
      };
    } catch (err: any) {
      return {
        props: {
          builderSections,
          invitationData: {
            error: err.message || 'There was an unexpected error',
            isValid: false,
            code,
          },
          treatments,
        },
      };
    }
  } else {
    // No invitation to fetch, return nothing for invitation
    return {
      props: {
        builderSections,
        treatments,
      },
    };
  }
};

async function getInvitationByCode(
  context: GetServerSidePropsContext,
  code: string
): Promise<Invitation> {
  const baseUrl = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  const headers: HeadersInit = new Headers();
  headers.set('cookie', context.req.headers.cookie || '');

  const response = await fetch(`${baseUrl}/api/v4/invitations/${code}`, {
    headers,
  });
  const result = await response.json();

  if (response.status !== 200) {
    // Look for possible error properties
    throw new Error(result?.error);
  }

  // Success
  const body = result as InvitationResponse;

  return {
    code,
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso,
    },
    ssoUrl: body.sso_url,
    email: body.email,
    id: body.id,
    status: body.status,
    createdBy: body.created_by,
  };
}
